import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, ArrowRight, Briefcase } from 'lucide-react';
import { CASE_STUDIES_HERO } from '@/data/caseStudies';
import { SlashMark } from '../AmdenMark';
import { scrollToId } from '@/lib/scroll';

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="hero-mask-line">
    <motion.span
      className="block"
      initial={reduce ? false : { y: '110%' }}
      animate={reduce ? undefined : { y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const CaseStudiesHero = () => {
  const reduce = useReducedMotion();

  return (
    <section aria-labelledby="case-studies-heading" data-testid="case-studies-hero" className="relative overflow-hidden bg-white">
      <SlashMark
        className="pointer-events-none absolute -right-16 top-10 h-[380px] w-[380px] opacity-[0.05]"
        bar="#0B1729"
        dot="#FFB800"
      />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-gold-100/50 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-6">
          <motion.p
            data-testid="case-studies-eyebrow"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-gold-700"
          >
            <span className="h-px w-8 bg-gold-600" aria-hidden="true" />
            {CASE_STUDIES_HERO.eyebrow}
          </motion.p>

          <h1
            id="case-studies-heading"
            data-testid="case-studies-heading"
            className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.1} reduce={reduce}>
              {CASE_STUDIES_HERO.headingA}
            </MaskedLine>
            <MaskedLine delay={0.22} reduce={reduce}>
              <span className="font-serif font-semibold italic text-gold-700">{CASE_STUDIES_HERO.headingAccent}</span>
            </MaskedLine>
          </h1>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
          >
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {CASE_STUDIES_HERO.body}
            </p>
            <p className="mt-4 max-w-xl border-l-2 border-gold-600 pl-4 text-sm font-semibold text-navy-800">
              {CASE_STUDIES_HERO.supportingLine}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => scrollToId(CASE_STUDIES_HERO.primaryCta.target)}
                data-testid="case-studies-explore-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-4 text-base font-bold text-white transition-colors hover:bg-navy-700"
              >
                {CASE_STUDIES_HERO.primaryCta.label}
                <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
              </button>
              <Link
                to={CASE_STUDIES_HERO.secondaryCta.href}
                data-testid="case-studies-discuss-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-7 py-[14px] text-base font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
              >
                {CASE_STUDIES_HERO.secondaryCta.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={reduce ? undefined : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-xl border border-slate-200 shadow-lift">
              <img
                src={CASE_STUDIES_HERO.image.src}
                alt={CASE_STUDIES_HERO.image.alt}
                width={CASE_STUDIES_HERO.image.width}
                height={CASE_STUDIES_HERO.image.height}
                fetchPriority="high"
                data-testid="case-studies-hero-image"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <motion.div
              data-testid="case-studies-stat-card"
              initial={reduce ? false : { opacity: 0, y: 24 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="absolute -bottom-7 left-4 flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-lift sm:left-8"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gold-100 text-gold-700">
                <Briefcase className="h-5 w-5" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {CASE_STUDIES_HERO.statCard.label}
                </span>
                <span className="mt-0.5 block text-2xl font-extrabold tracking-tight text-navy-900">
                  {CASE_STUDIES_HERO.statCard.value}
                </span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
